import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import selectors from "selectors";
import { fetchPatientsEquipment } from "actions/products";
import { Button } from "components/ui_new/index";
import { Page, Card } from "components/ui";
import { useHistory } from "react-router-dom";
import "./products.scss";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";

const Products = () => {
  const translate = useTranslate();
  useDocumentTitle(translate("myProducts"), "Wellsky");
  const dispatch = useDispatch();
  const equipment = useSelector(selectors.getPatientsEquipment);
  const [isFetching, setIsFetching] = useState(false);
  const [equipmentToShow, setEquipmentToShow] = useState([]);
  useEffect(() => {
    setIsFetching(true);
    dispatch(fetchPatientsEquipment()).finally(() => {
      setIsFetching(false);
    });
  }, [dispatch]);

  useEffect(() => {
    if (equipment.length > 0 && equipmentToShow.length === 0) {
      setEquipmentToShow(equipment.slice(0, 4));
    }
  }, [equipment]);

  const handleLoadMore = () => {
    const currentLength = equipmentToShow.length;
    const nextBatch = equipment.slice(currentLength, currentLength + 4);
    setEquipmentToShow([...equipmentToShow, ...nextBatch]);

    setTimeout(() => {
      const firstNewProduct = document.querySelector(
        `[data-product-index="${currentLength}"]`
      );
      if (firstNewProduct) {
        firstNewProduct.focus();
      }
    }, 100);
  };

  const hasMoreProducts = equipmentToShow.length < equipment.length;
  return (
    <div className="products-page-container">
      <Page.Container>
        <Page.Header title={translate("myProducts")} goBack={"/"} />
        <Page.Content
          isFetching={isFetching}
          styles={{ gap: "24px", flexFlow: "column wrap" }}
        >
          <div className="title-products-page">
            <h1>{translate("welcomeExclamationMark")}</h1>
            {equipmentToShow.length > 0 ? (
              <h2>{translate("theseAreYourProducts")}</h2>
            ) : (
              <h2>{translate("noProducts")}</h2>
            )}
          </div>
          <div className="products-container">
            {equipmentToShow.length > 0 &&
              equipmentToShow.map((eq, index) => (
                <Product key={eq.product_id} {...eq} index={index}/>
              ))}
          </div>
          {hasMoreProducts && (
            <div className="load-more-container">
              <Button
                variant="text"
                onClick={handleLoadMore}
                className="load-more-button"
                aria-label={`${translate("seeMore")} products`}
              >
                {translate("seeMore") || "See More"}
              </Button>
            </div>
          )}
        </Page.Content>
      </Page.Container>
    </div>
  );
};

export default Products;

const Product = ({
  product_photo,
  product_id,
  image,
  name,
  spanish_information,
  manufacturer,
  type,
  spanish_type,
  // autofit_selection,
  purchase_date,
  information,
  index
}) => {
  const speaksSpanish = useSelector(selectors.getSpeaksSpanish);
  const translate = useTranslate();
  const history = useHistory();
  const videos = useSelector(state =>
    selectors.getProductVideos(state, product_id)
  );
  return (
    <Card
      className={"product-card-container"}
      aria-label={`${!speaksSpanish ? type : (spanish_type ?? type)} product card`}
      data-product-index={index}
      tabIndex={-1}
    >
      <div className="product-card">
        <div>
          {product_photo || image ? (
            <img
              className="product-img"
              src={product_photo ? product_photo : image}
              alt=""
              role="presentation"
            />
          ) : (
            <div className="product-img-placeholder" role="presentation">
              <div className="product-img-placeholder-text">
                {translate("noPhoto")}
              </div>
            </div>
          )}
        </div>
        <div className="product-card-content">
          <div className="product-content">
            <h3 className="product-name" aria-label={!speaksSpanish ? type : (spanish_type ?? type)}>
              {!speaksSpanish ? type : (spanish_type ?? type)}
            </h3>
            <div className="product-manufacturer" aria-label={!speaksSpanish ? information : spanish_information}>
              {!speaksSpanish
                ? information
                : (spanish_information ?? information)}
            </div>
          </div>
          <Button
            color="primary"
            variant="contained"
            type="button"
            className="ui_button"
            disabled={videos.length == 0}
            onClick={() => {
              history.push(`products/${product_id}/videos`);
            }}
            aria-label={`${translate("watchVideos")} ${!speaksSpanish ? type : (spanish_type ?? type)}`}
          >
            {translate("watchVideos")}
          </Button>
        </div>
      </div>
    </Card>
  );
};

Product.propTypes = {
  product_photo: PropTypes.string,
  product_id: PropTypes.string.isRequired,
  image: PropTypes.string,
  name: PropTypes.string,
  spanish_information: PropTypes.string,
  manufacturer: PropTypes.string,
  type: PropTypes.string,
  autofit_selection: PropTypes.bool,
  purchase_date: PropTypes.string
};
